import type { ClientCommand, GameState, ShotResult } from '../shared/types';
import { LEGACY_ROOM_ID, validRoomId } from '../shared/rooms';

export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'offline';
export interface NetworkCallbacks {
  state: (state: GameState, shot?: ShotResult, serverTime?: number, ackSeq?: number) => void;
  status: (status: ConnectionStatus) => void;
  error: (message: string) => void;
  hello?: (playerId: string) => void;
  clock?: (serverNow: number) => void;
}

const ID_KEY = 'ddt2009.playerId';
const TOKEN_KEY = 'ddt2009.sessionToken';
const TURN_COMMANDS = new Set(['move', 'aim', 'useProp', 'fire', 'skip']);
export const roomSessionKey = (roomId: string) => `${TOKEN_KEY}.${roomId}`;
const NAME_KEY = 'ddt2009.nickname';
const SEQ_KEY = 'ddt2009.commandSeq';
const MAX_STATE_FAILURES = 4;
const STATE_TIMEOUT_MS = 10_000;
export const savedNickname = () => localStorage.getItem(NAME_KEY) ?? '';
export function stablePlayerId(): string {
  let id = localStorage.getItem(ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(ID_KEY, id);
  }
  return id;
}

export class RoomConnection {
  readonly playerId = stablePlayerId();
  private socket: WebSocket | null = null;
  private name = '';
  private stopped = false;
  private connectedOnce = false;
  private lastPhase: GameState['phase'] | null = null;
  private attempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private stateTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private generation = 0;
  private seq = Number(localStorage.getItem(SEQ_KEY) ?? '0');
  private lastPong = Date.now();
  private serverOffset: number | null = null;
  private bestRoundTrip = Infinity;
  private selectedRoomId = LEGACY_ROOM_ID;
  private connectionId = '';
  private authoritativeTurn: { round: number; number: number } | null = null;
  status: ConnectionStatus = 'offline';
  get commandSeq(): number { return this.seq; }
  get roomId(): string { return this.selectedRoomId; }

  constructor(private callbacks: NetworkCallbacks) {}

  connect(name: string, roomId = LEGACY_ROOM_ID): boolean {
    if (!validRoomId(roomId)) { this.callbacks.error('房间链接无效，请从大厅选择房间'); return false; }
    // A user-created connection has a new fence; automatic retries in open()
    // retain it so an exit during recovery can still release the old seat.
    this.connectionId = crypto.randomUUID();
    this.generation += 1;
    this.releaseSocket();
    this.name = name.trim().slice(0, 16) || '弹友';
    this.selectedRoomId = roomId;
    localStorage.setItem('ddt2009.roomId', roomId);
    localStorage.setItem(NAME_KEY, this.name);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.connectedOnce = false;
    this.lastPhase = null;
    this.attempts = 0;
    this.serverOffset = null;
    this.bestRoundTrip = Infinity;
    this.stopped = false;
    this.open();
    return true;
  }

  private setStatus(status: ConnectionStatus) {
    this.status = status;
    this.callbacks.status(status);
  }

  private releaseSocket() {
    if (this.heartbeat) clearInterval(this.heartbeat);
    if (this.stateTimer) clearTimeout(this.stateTimer);
    this.heartbeat = null;
    this.stateTimer = null;
    const socket = this.socket;
    this.socket = null;
    this.authoritativeTurn = null;
    if (socket) {
      socket.onopen = socket.onmessage = socket.onerror = socket.onclose = null;
      socket.close(1000, 'Connection ended');
    }
  }

  private open() {
    if (this.stopped) return;
    this.setStatus(this.connectedOnce ? 'reconnecting' : 'connecting');
    const url = new URL('/api/ws', location.href);
    url.protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    url.searchParams.set('playerId', this.playerId);
    url.searchParams.set('name', this.name);
    url.searchParams.set('roomId', this.selectedRoomId);
    url.searchParams.set('connectionId', this.connectionId);
    let token = localStorage.getItem(roomSessionKey(this.selectedRoomId));
    if (!token && this.selectedRoomId === LEGACY_ROOM_ID) {
      token = localStorage.getItem(TOKEN_KEY);
      if (token) localStorage.setItem(roomSessionKey(this.selectedRoomId), token);
    }
    if (token) url.searchParams.set('token', token);
    const socket = new WebSocket(url);
    this.socket = socket;
    const generation = this.generation;
    let receivedState = false;
    const current = () => socket === this.socket && generation === this.generation && !this.stopped;
    const disconnected = (code: number) => {
      if (!current()) return;
      this.releaseSocket();
      if (code === 4001) {
        this.stopped = true; this.setStatus('offline');
        this.callbacks.error('这个弹友已在另一个窗口入房，请回到那个窗口继续。');
        return;
      }
      if (this.lastPhase && this.lastPhase !== 'playing') {
        this.stopped = true; this.setStatus('offline');
        this.callbacks.error('连接已断开，你已退出房间。可以点击进入房间重新加入。');
        return;
      }
      if (!receivedState) this.attempts += 1;
      if (this.attempts >= MAX_STATE_FAILURES) {
        this.stopped = true; this.setStatus('offline');
        this.callbacks.error(this.lastPhase === 'playing'
          ? '战斗连接已中断，暂时无法恢复。请点击进入房间重新加入。'
          : '暂时无法进入房间，可能已满员或连接不可用。请稍后点击进入房间再试。');
        return;
      }
      this.setStatus('reconnecting');
      const delay = Math.min(8_000, 700 * 2 ** Math.min(Math.max(0, this.attempts - 1), 4)) + Math.random() * 250;
      this.reconnectTimer = setTimeout(() => {
        if (generation !== this.generation || this.stopped) return;
        this.reconnectTimer = null;
        this.open();
      }, delay);
    };
    // Opening the transport or receiving hello/pong does not prove the seat
    // was restored. Bound attempts that never receive authoritative state too.
    this.stateTimer = setTimeout(() => disconnected(0), STATE_TIMEOUT_MS);
    socket.onopen = () => {
      if (!current()) return;
      this.lastPong = Date.now();
      this.raw({ type: 'hello', playerId: this.playerId });
      this.raw({ type: 'ping', sentAt: Date.now() });
      if (this.heartbeat) clearInterval(this.heartbeat);
      this.heartbeat = setInterval(() => {
        if (!current()) return;
        if (Date.now() - this.lastPong > 50_000) { socket.close(); return; }
        this.raw({ type: 'ping', sentAt: Date.now() });
      }, 15_000);
    };
    socket.onmessage = (event) => {
      if (!current()) return;
      this.lastPong = Date.now();
      if (event.data === 'pong') return;
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'state') {
          this.lastPhase = message.state.phase;
          const turn = message.state.turn;
          this.authoritativeTurn = message.state.phase === 'playing' && Number.isSafeInteger(message.state.round) && Number.isSafeInteger(turn?.number)
            ? { round: message.state.round, number: turn.number } : null;
          receivedState = true;
          this.attempts = 0;
          this.connectedOnce = true;
          if (this.stateTimer) clearTimeout(this.stateTimer);
          this.stateTimer = null;
          if (this.status !== 'connected') this.setStatus('connected');
          const serverNow = this.serverOffset === null ? message.serverTime : Date.now() + this.serverOffset;
          this.callbacks.state(message.state, message.shot, serverNow, message.ackSeq);
        } else if (message.type === 'pong' && Number.isFinite(message.sentAt) && Number.isFinite(message.serverTime)) {
          const receivedAt = Date.now(), roundTrip = receivedAt - message.sentAt;
          if (roundTrip >= 0 && roundTrip < 10_000 && roundTrip <= this.bestRoundTrip + 30) {
            this.bestRoundTrip = Math.min(this.bestRoundTrip, roundTrip);
            const offset = message.serverTime + roundTrip / 2 - receivedAt;
            this.serverOffset = this.serverOffset === null ? offset : this.serverOffset * .8 + offset * .2;
            this.callbacks.clock?.(receivedAt + this.serverOffset);
          }
        } else if (message.type === 'hello') {
          if (message.sessionToken) localStorage.setItem(roomSessionKey(this.selectedRoomId), message.sessionToken);
          this.callbacks.hello?.(message.playerId ?? this.playerId);
        } else if (message.type === 'error') {
          this.callbacks.error(message.message ?? '操作未完成，请再试一次');
        } else if (message.type === 'ping') {
          this.raw({ type: 'pong', time: message.time });
        }
      } catch {
        this.callbacks.error('房间消息暂时无法读取，正在等待同步');
      }
    };
    socket.onerror = () => { /* onclose schedules recovery. */ };
    socket.onclose = (event) => disconnected(event.code);
  }

  send(command: ClientCommand): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.callbacks.error(this.status === 'offline' ? '尚未入房，请点击进入房间重新加入' : '连接正在恢复，请稍候');
      return false;
    }
    const turn = TURN_COMMANDS.has(command.type) ? this.authoritativeTurn : null;
    if (TURN_COMMANDS.has(command.type) && !turn) { this.callbacks.error('正在等待当前回合同步，请稍候'); return false; }
    this.seq += 1;
    localStorage.setItem(SEQ_KEY, String(this.seq));
    this.raw({ type: 'command', seq: this.seq, command, ...(turn ? { turn: { ...turn } } : {}) });
    return true;
  }

  private raw(message: unknown) {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(message));
  }

  close() {
    // A deliberate exit forfeits the seat. Transport failure continues to use
    // releaseSocket and retains the battle reconnect grace period.
    const sentLeave = this.socket?.readyState === WebSocket.OPEN && this.send({ type: 'leave' });
    const token = localStorage.getItem(roomSessionKey(this.selectedRoomId));
    const roomId = this.selectedRoomId, connectionId = this.connectionId;
    this.stopped = true;
    this.generation += 1;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.releaseSocket();
    this.setStatus('offline');
    // A deliberate exit while reconnecting has no open socket to carry leave.
    // Its room token and connection fence still authorize releasing that seat.
    if (!sentLeave && token && connectionId) void this.leaveOffline(roomId, token, connectionId, this.generation);
  }

  private async leaveOffline(roomId: string, token: string, connectionId: string, generation: number): Promise<void> {
    let detail = '';
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const url = new URL('/api/leave', location.href);
        url.searchParams.set('roomId', roomId);
        const response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ playerId: this.playerId, token, connectionId }), keepalive: true, signal: AbortSignal.timeout(5_000) });
        if (response.ok) return;
        const result = await response.json() as { error?: string };
        detail = result.error ?? '连接暂时不可用';
        if (response.status >= 400 && response.status < 500 && response.status !== 429) break;
      } catch { detail = '连接暂时不可用'; }
      if (attempt < 2) await new Promise<void>(resolve => setTimeout(resolve, 750 * (attempt + 1)));
    }
    // A failed exit from an earlier room must not interrupt a new connection.
    if (generation === this.generation && this.status === 'offline') {
      this.callbacks.error(`退出房间尚未确认：${detail}。请重新进入该房间后再退出。`);
    }
  }
}
